import { memo, useRef } from "react";
import Grid from "@mui/material/Grid";
import QuantityInput from "../../common/QuantityInput";
import {
  AutocompleteInput,
  maxLength,
  minLength,
  number,
  ReferenceInput,
  required,
} from "react-admin";
import CodeDetailInput from "../../common/CodeDetailInput";
import {
  inputTextProduct,
  matchSuggestionProduct,
  OptionRendererProduct,
} from "../../../common/OptionRendererProduct";
import ProductInput from "../../../purchaseOrders/common/ProductInput";
import { useFormContext, useWatch } from "react-hook-form";
import useEnterToAddRowAndFocus from "../../../../utils/useEnterToAddRowAndFocus";

const uniqueItem = (value, allValues, elm) => {
  if (!allValues.mutation_repacking_items) return undefined;
  let count = 0;
  for (let i = 0; i < allValues.mutation_repacking_items.length; i++) {
    const elm = allValues.mutation_repacking_items[i];
    if (value === elm.product_id) {
      if (count > 0) {
        return "Duplicate is not allowed";
      }
      count += 1;
    }
  }
  return undefined;
};

const validateCode = [required(), minLength(36), maxLength(36), uniqueItem];
const validateLocation = [required(), minLength(36), maxLength(36)];
const validateQuantity = [required(), number()];

const CstInput = ({
  quantity,
  formData,
  idSource,
  quantitySource,
  nameSource,
  qualitySource,
  priceSource,
  locationSource,
}) => {
  const { setValue } = useFormContext();

  // 🔹 Ref ke input Quantity
  const quantityRef = useRef(null);

  // 🔹 Aktifkan fungsi Enter → tambah baris dan fokus
  useEnterToAddRowAndFocus(
    quantityRef,
    undefined,
    'input[name*="product_id"]'
  );

  const onCodeChangeHandler = async (code) => {
    if (code.length !== 36) {
      setValue(quantitySource, "");
      setValue(locationSource, "");
      setValue(nameSource, "");
      setValue(qualitySource, "");
      setValue(priceSource, "");
    }
  };

  // 🔹 Kecualikan produk yang sudah dipilih
  const exclude = {};
  const exclude_ids = [];
  const items = formData.mutation_repacking_items || [];
  for (let i = 0; i < items.length; i++) {
    if (items[i].product_id) {
      exclude_ids.push(items[i].product_id);
    }
  }
  if (exclude_ids.length) {
    exclude["products.exclude_ids"] = exclude_ids;
  }

  return (
    <Grid
      container
      spacing={2}
      sx={{
        "& .MuiGrid-item": { py: 0 },
        mt: 0,
      }}
    >
      {/* 🔹 Kode Produk */}
      <Grid item xs={12} lg={3}>
        <ProductInput
          fullWidth
          source={idSource}
          variant="outlined"
          validate={validateCode}
          label="mine.label.product_code"
          onChange={onCodeChangeHandler}
          dependOn="date"
          reset={[
            quantitySource,
            locationSource,
            nameSource,
            qualitySource,
            priceSource,
          ]}
          detailType="all"
          defaultFilters={{ "products.active": 1 }}
          exclude={exclude}
          optionText={<OptionRendererProduct />}
          inputText={inputTextProduct}
          matchSuggestion={matchSuggestionProduct}
          helperText={false}
        />
      </Grid>

      {/* 🔹 Gudang */}
      <Grid item xs={12} lg={1.5}>
        <ReferenceInput
          source={locationSource}
          reference="warehouses"
          perPage={10}
          sort={{ field: "warehouses.created_at", order: "ASC" }}
        >
          <AutocompleteInput
            variant="outlined"
            label="mine.label.warehouse"
            fullWidth
            validate={validateLocation}
            optionText="name"
            helperText={false}
          />
        </ReferenceInput>
      </Grid>

      {/* 🔹 Quantity */}
      <Grid item xs={12} lg={1} ref={quantityRef}>
        <QuantityInput
          variant="outlined"
          source={quantitySource}
          label="mine.label.quantity"
          fullWidth
          validate={validateQuantity}
          quantity={quantity}
          formData={formData}
          helperText={false}
        />
      </Grid>

      {/* 🔹 Detail kode */}
      <CodeDetailInput
        nameSource={nameSource}
        qualitySource={qualitySource}
        priceSource={priceSource}
        quantitySource={quantitySource}
        dependOn={idSource}
      />
    </Grid>
  );
};

// 🔹 Optimisasi: re-render hanya jika data berbeda
const propsAreEqual = (prevProps, nextProps) => {
  return (
    prevProps.formData === nextProps.formData &&
    prevProps.idSource === nextProps.idSource &&
    prevProps.quantitySource === nextProps.quantitySource
  );
};

const MemoizedInput = memo(CstInput, propsAreEqual);

// 🔹 Wrapper untuk digunakan di ArrayInput
const Inputs = ({ formData, scopedFormData, getSource, ...rest }) => {
  return (
    <MemoizedInput
      {...scopedFormData}
      formData={formData}
      idSource={getSource("product_id")}
      quantitySource={getSource("quantity")}
      nameSource={getSource("name")}
      qualitySource={getSource("quality")}
      priceSource={getSource("price")}
      locationSource={getSource("warehouse_id")}
    />
  );
};

export default Inputs;
